import { z } from "zod";
import { createEndpoint, createEndpointWithPagination, StandardSuccessResponseSchema } from "../createEndpoint";
import { HttpClient, QueryParams } from "../httpClient";
import {
    DraftLeadAdminResponseSchema,
    AdminSubmitDraftLeadResponseSchema,
    AdminListDraftLeadsQueryParams,
    AdminCreateDraftLeadRequest,
    AdminUpdateDraftLeadRequest,
} from "../schemas/draft-leads";

export function createAdminDraftLeadsApi(http: HttpClient, baseUrl: string) {
    const adminBaseUrl = `${baseUrl}/admin/draft-leads`;

    return {
        /**
         * List all draft leads with optional filtering by status
         * GET /api/v2/admin/draft-leads
         */
        listDraftLeads: createEndpointWithPagination<
            z.infer<typeof DraftLeadAdminResponseSchema>,
            unknown,
            unknown,
            AdminListDraftLeadsQueryParams
        >(http, "GET", "", {
            base: adminBaseUrl,
            itemSchema: DraftLeadAdminResponseSchema,
        }),

        /**
         * Get a single draft lead by ID
         * GET /api/v2/admin/draft-leads/:id
         */
        getDraftLead: createEndpoint<
            z.infer<typeof DraftLeadAdminResponseSchema>,
            unknown,
            QueryParams,
            { id: number }
        >(http, "GET", "/:id", {
            base: adminBaseUrl,
            responseSchema: DraftLeadAdminResponseSchema,
            params: ["id"],
        }),

        /**
         * Create a new draft lead as an admin
         * POST /api/v2/admin/draft-leads
         * 
         * Tracks which admin created the draft
         */
        createDraftLead: createEndpoint<
            z.infer<typeof DraftLeadAdminResponseSchema>,
            AdminCreateDraftLeadRequest,
            QueryParams
        >(http, "POST", "", {
            base: adminBaseUrl,
            responseSchema: DraftLeadAdminResponseSchema,
        }),

        /**
         * Update an existing draft lead
         * PUT /api/v2/admin/draft-leads/:id
         * 
         * Cannot update a submitted draft lead
         */
        updateDraftLead: createEndpoint<
            z.infer<typeof DraftLeadAdminResponseSchema>,
            AdminUpdateDraftLeadRequest,
            QueryParams,
            { id: number }
        >(http, "PUT", "/:id", {
            base: adminBaseUrl,
            responseSchema: DraftLeadAdminResponseSchema,
            params: ["id"],
        }),

        /**
         * Soft delete a draft lead
         * DELETE /api/v2/admin/draft-leads/:id
         */
        deleteDraftLead: createEndpoint<
            z.infer<typeof StandardSuccessResponseSchema>,
            unknown,
            QueryParams,
            { id: number }
        >(http, "DELETE", "/:id", {
            base: adminBaseUrl,
            responseSchema: StandardSuccessResponseSchema,
            params: ["id"],
        }),

        /**
         * Submit a draft lead on behalf of any user
         * POST /api/v2/admin/draft-leads/:id/submit
         * 
         * Admins can submit for existing users without requiring their authentication
         */
        submitDraftLead: createEndpoint<
            z.infer<typeof AdminSubmitDraftLeadResponseSchema>,
            unknown,
            QueryParams,
            { id: number }
        >(http, "POST", "/:id/submit", {
            base: adminBaseUrl,
            responseSchema: AdminSubmitDraftLeadResponseSchema,
            params: ["id"],
        }),
    };
}

export type AdminDraftLeadsApi = ReturnType<typeof createAdminDraftLeadsApi>;
